import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../hooks/useAuthContext';
import { supabase } from '../../config/supabase';

export default function SuperAdminProtectedRoute({ children, allowedRoles = ['superadmin'] }) {
  const { session, isLoading } = useAuthContext();
  const navigate = useNavigate();
  const [checkingRole, setCheckingRole] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      if (isLoading) return;

      if (!session) {
        setHasAccess(false);
        setCheckingRole(false);
        navigate('/superadmin/login');
        return;
      }

      try {
        // Fetch user profile to check role
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, is_admin, is_superadmin')
          .eq('id', session.user.id)
          .single();

        console.log('SuperAdminProtectedRoute Debug:', {
          profile,
          profileError,
          is_superadmin: profile?.is_superadmin,
          is_superadmin_type: typeof profile?.is_superadmin,
          is_admin: profile?.is_admin,
          is_admin_type: typeof profile?.is_admin
        });

        if (profileError) {
          console.error('Error fetching profile:', profileError);
          navigate('/superadmin/login');
          return;
        }

        // Check if user has superadmin role
        const isSuperAdmin = profile.is_superadmin === true;

        if (isSuperAdmin && allowedRoles.includes('superadmin')) {
          console.log('✅ User is SuperAdmin, granting access');
          setHasAccess(true);
        } else {
          console.log('❌ User is not SuperAdmin, checking admin status');
          // No superadmin access - redirect based on their role
          if (profile.is_admin === true) {
            console.log('⚠️ User is Admin, redirecting to /admin');
            // Regular admin - redirect to admin dashboard
            navigate('/admin');
          } else {
            console.log('❌ User has no privileges, redirecting to login');
            // No admin privileges - redirect to login
            navigate('/superadmin/login');
          }
        }
      } catch (err) {
        console.error('Error checking role:', err);
        navigate('/superadmin/login');
      } finally {
        setCheckingRole(false);
      }
    };

    checkAccess();
  }, [session?.user?.id, isLoading, navigate, allowedRoles]);

  if ((isLoading || checkingRole) && !hasAccess) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh'
      }}>
        Loading...
      </div>
    );
  }

  if (!session || !hasAccess) {
    return null;
  }

  return <>{children}</>;
}

